import { Dispatch, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import {
  getBackupChecks,
  getBackupCheck,
  GetBackupChecksProps,
  GetBackupChecksResponse,
  GetBackupCheckProps,
  finalizeBackupCheck,
  printBackupCheck,
  updateBackupCheckChequeNumber,
  cancelBackupCheck,
  getPrinterStatus,
} from "@/services/backup-checks.service";
import { BackupCheckDetail, PrintCheckRequest } from "./backup-checks.schema";
import { getBackupChecksFilter } from "./filter-obj";
import { handlePermissionError } from "@/utils/permission-check";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { AppRoutes } from "@/constants/routes";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("backup_date", undefined);
  setValue("account_number", undefined);
  setValue("backup_amount", "");
  setValue("backup_requester", "");
  setValue("estados", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<
    SetStateAction<Partial<GetBackupChecksProps> | undefined>
  >;
  fetchBackupChecks: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchBackupChecks,
  setOpenFilters,
  navigate,
  fetchPermissions,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetBackupChecksProps> =
      getBackupChecksFilter(event);
    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

    await fetchBackupChecks(filterObj);
    setOpenFilters(false);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.BACKUP_CHECKS,
    );

    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron aplicar los filtros",
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  }
};

interface BaseActionHandlerProps {
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export interface FinalizeBackupCheckRequestHandlerProps
  extends BaseActionHandlerProps {
  id: number;
  onSuccess?: () => Promise<void> | void;
}

export const finalizeBackupCheckRequestHandler = async ({
  id,
  navigate,
  fetchPermissions,
  onSuccess,
}: FinalizeBackupCheckRequestHandlerProps) => {
  try {
    await finalizeBackupCheck({ id });

    toaster.create({
      type: "success",
      title: "Solicitud finalizada exitosamente.",
    });

    await onSuccess?.();
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.BACKUP_CHECKS,
    );

    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo finalizar la solicitud.",
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  }
};

export interface CancelBackupCheckHandlerProps extends BaseActionHandlerProps {
  id: number;
  event?: FieldValues;
  onSuccess?: () => Promise<void> | void;
}

export const cancelBackupCheckHandler = async ({
  id,
  event,
  navigate,
  fetchPermissions,
  onSuccess,
}: CancelBackupCheckHandlerProps) => {
  try {
    await cancelBackupCheck({
      id,
      idMotivo: event?.message_motive?.[0],
      mensaje: event?.request_detail,
    });

    toaster.create({
      type: "success",
      title: "Solicitud cancelada exitosamente.",
    });

    await onSuccess?.();
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.BACKUP_CHECKS,
    );

    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo imprimir el cheque.",
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  }
};

export interface PrintBackupCheckHandlerProps extends BaseActionHandlerProps {
  printRequest: PrintCheckRequest;
  onSuccess?: () => Promise<void> | void;
  setOpenPrint: (open: boolean) => void;
}

export const printBackupCheckHandler = async ({
  printRequest,
  onSuccess,
  setOpenPrint,
}: PrintBackupCheckHandlerProps) => {
  try {
    const statusResponse = await getPrinterStatus();

    if (statusResponse?.status !== 200) {
      toaster.create({
        type: "error",
        title: "La impresora no está conectada o no está lista.",
      });
      setOpenPrint(false);
      return
    }

    await printBackupCheck(printRequest);
    await onSuccess?.();
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo imprimir el cheque.",
    );
          setOpenPrint(false);
    toaster.create({
      type: "error",
      title: errorMessage,
    });
    throw error;
  }
};

export interface FinalizeBackupCheckHandlerProps
  extends BaseActionHandlerProps {
  id: number;
  chequeNumber: string;
  onSuccess?: () => Promise<void> | void;
}

export const updateBackupCheckHandler = async ({
  id,
  chequeNumber,
  navigate,
  fetchPermissions,
  onSuccess,
}: FinalizeBackupCheckHandlerProps) => {
  try {
    await updateBackupCheckChequeNumber({
      id,
      chequeNumber: String(chequeNumber ?? "").trim(),
    });

    toaster.create({
      type: "success",
      title: "N° de cheque guardado correctamente.",
    });

    await onSuccess?.();
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.BACKUP_CHECKS,
    );
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudo guardar el N° de cheque.",
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title: errorMessage,
      });
    }
    throw error;
  }
};

interface FetchBackupChecksHandlerProps {
  showLoad?: boolean;
  setLoadingBackupChecks: Dispatch<SetStateAction<boolean>>;
  setBackupChecks: Dispatch<SetStateAction<GetBackupChecksResponse | null>>;
  filters?: Partial<GetBackupChecksProps>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchBackupChecksHandler = async ({
  showLoad,
  setLoadingBackupChecks,
  setBackupChecks,
  filters,
  navigate,
  fetchPermissions,
}: FetchBackupChecksHandlerProps) => {
  try {
    if (showLoad) setLoadingBackupChecks(true);

    const response = await getBackupChecks({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": filters?.["P.Records"] ?? 10,
    });

    setBackupChecks(response);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.BACKUP_CHECKS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ?? "No se pudo cargar el listado.",
      });
    }
    throw error;
  } finally {
    setLoadingBackupChecks(false);
  }
};

export interface FetchBackupCheckDetailHandlerProps {
  showLoad?: boolean;
  setLoadingBackupCheck: Dispatch<SetStateAction<boolean>>;
  setBackupCheck: Dispatch<
    SetStateAction<BackupCheckDetail | null | undefined>
  >;
  id?: string;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchBackupCheckDetailHandler = async ({
  showLoad,
  setLoadingBackupCheck,
  setBackupCheck,
  id,
  navigate,
  fetchPermissions,
}: FetchBackupCheckDetailHandlerProps) => {
  try {
    if (showLoad) setLoadingBackupCheck(true);

    const response = await getBackupCheck({
      id: Number(id),
    } as GetBackupCheckProps);

    setBackupCheck(response);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.BACKUP_CHECKS,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.response?.data?.message ?? "No se pudo cargar el detalle.",
      });
    }
    throw error;
  } finally {
    setLoadingBackupCheck(false);
  }
};
